import { ref, computed } from 'vue';
import { visualToLayout } from 'dashboard/helper/dashboardZoom';

export const useImageZoom = imageRef => {
  const MAX_ZOOM_LEVEL = 4;
  const ZOOM_LEVELS = Array.from(
    { length: MAX_ZOOM_LEVEL },
    (_, index) => index + 1
  );
  const DOUBLE_CLICK_ZOOM_LEVELS = [1, 2, 3];
  const WHEEL_ZOOM_INTERVAL = 100;
  const DEFAULT_IMG_TRANSFORM_ORIGIN = 'center center';

  const zoomScale = ref(ZOOM_LEVELS[0]);
  const imgTransformOriginPoint = ref(DEFAULT_IMG_TRANSFORM_ORIGIN);
  const activeImageRotation = ref(0);
  const panOffset = ref({ x: 0, y: 0 });
  const isDragging = ref(false);
  const dragStart = ref(null);
  let wheelZoomDirection = 0;
  let lastWheelZoomAt = Number.NEGATIVE_INFINITY;

  const imageWrapperStyle = computed(() => ({
    transform: `rotate(${activeImageRotation.value}deg)`,
  }));

  const imageCursor = computed(() => {
    if (zoomScale.value === ZOOM_LEVELS[0]) return 'default';
    return isDragging.value ? 'grabbing' : 'grab';
  });

  const imageStyle = computed(() => ({
    transform: `translate3d(${panOffset.value.x}px, ${panOffset.value.y}px, 0) scale(${zoomScale.value})`,
    cursor: imageCursor.value,
    transformOrigin: imgTransformOriginPoint.value,
  }));

  const cancelPointerDrag = () => {
    const pointerId = dragStart.value?.pointerId;
    if (
      pointerId !== undefined &&
      imageRef.value?.hasPointerCapture?.(pointerId)
    ) {
      imageRef.value.releasePointerCapture(pointerId);
    }
    isDragging.value = false;
    dragStart.value = null;
  };

  const resetTransformOrigin = () => {
    imgTransformOriginPoint.value = DEFAULT_IMG_TRANSFORM_ORIGIN;
  };

  const resetPan = () => {
    panOffset.value = { x: 0, y: 0 };
    cancelPointerDrag();
  };

  const resetWheelZoomIntent = () => {
    wheelZoomDirection = 0;
    lastWheelZoomAt = Number.NEGATIVE_INFINITY;
  };

  const imageDimensions = () => {
    if (!imageRef.value) return { width: 0, height: 0 };

    const rect = imageRef.value.getBoundingClientRect();
    return {
      width: imageRef.value.clientWidth || rect.width / zoomScale.value,
      height: imageRef.value.clientHeight || rect.height / zoomScale.value,
    };
  };

  const clampPan = (x, y, scale = zoomScale.value) => {
    if (scale === ZOOM_LEVELS[0]) return { x: 0, y: 0 };

    const { width, height } = imageDimensions();
    const maxX = (width * (scale - 1)) / 2;
    const maxY = (height * (scale - 1)) / 2;

    return {
      x: Math.max(-maxX, Math.min(maxX, x)),
      y: Math.max(-maxY, Math.min(maxY, y)),
    };
  };

  const setZoomScale = scale => {
    const nextLevel = Math.max(
      ZOOM_LEVELS[0],
      Math.min(MAX_ZOOM_LEVEL, Number(scale) || ZOOM_LEVELS[0])
    );

    zoomScale.value = nextLevel;
    panOffset.value = clampPan(panOffset.value.x, panOffset.value.y, nextLevel);
    resetWheelZoomIntent();

    if (nextLevel === ZOOM_LEVELS[0]) resetPan();
  };

  const setZoomLevel = level => {
    setZoomScale(ZOOM_LEVELS.includes(level) ? level : ZOOM_LEVELS[0]);
  };

  const onRotate = type => {
    if (!imageRef.value) return;

    const rotation = type === 'clockwise' ? 90 : -90;
    activeImageRotation.value = (activeImageRotation.value + rotation) % 360;
    setZoomLevel(ZOOM_LEVELS[0]);
    resetTransformOrigin();
  };

  const onZoom = direction => {
    if (!imageRef.value || direction === 0) return;

    const currentIndex = ZOOM_LEVELS.indexOf(zoomScale.value);
    const nextIndex = Math.max(
      0,
      Math.min(ZOOM_LEVELS.length - 1, currentIndex + Math.sign(direction))
    );
    setZoomLevel(ZOOM_LEVELS[nextIndex]);
  };

  const onDoubleClickZoomImage = event => {
    if (!imageRef.value) return;
    event.preventDefault();

    const currentIndex = DOUBLE_CLICK_ZOOM_LEVELS.indexOf(zoomScale.value);
    const nextIndex = (currentIndex + 1) % DOUBLE_CLICK_ZOOM_LEVELS.length;
    setZoomLevel(DOUBLE_CLICK_ZOOM_LEVELS[nextIndex]);
  };

  const onWheelImageZoom = event => {
    if (!imageRef.value || event.deltaY === 0) return;
    event.preventDefault();

    const direction = event.deltaY > 0 ? -1 : 1;
    const eventTime = Number.isFinite(event.timeStamp)
      ? event.timeStamp
      : Date.now();
    const shouldZoomImmediately = direction !== wheelZoomDirection;
    const hasZoomIntervalElapsed =
      eventTime - lastWheelZoomAt >= WHEEL_ZOOM_INTERVAL;
    if (!shouldZoomImmediately && !hasZoomIntervalElapsed) return;

    onZoom(direction);
    wheelZoomDirection = direction;
    lastWheelZoomAt = eventTime;
  };

  const onPointerDown = event => {
    if (
      !imageRef.value ||
      zoomScale.value === ZOOM_LEVELS[0] ||
      event.button !== 0
    ) {
      return;
    }

    if (event.pointerType !== 'touch') event.preventDefault();
    event.currentTarget?.setPointerCapture?.(event.pointerId);
    isDragging.value = true;
    dragStart.value = {
      pointerId: event.pointerId,
      x: event.clientX,
      y: event.clientY,
      panX: panOffset.value.x,
      panY: panOffset.value.y,
    };
  };

  const onPointerMove = event => {
    if (!isDragging.value || dragStart.value?.pointerId !== event.pointerId) {
      return;
    }

    event.preventDefault();
    // Aceleres 2026-08-25: `panOffset` sai num `translate3d(...px)` e os limites
    // vem de `clientWidth` — ambos LAYOUT. So o delta do ponteiro chega em
    // VISUAL, por isso e o unico que converte. Ver helper/dashboardZoom.js.
    panOffset.value = clampPan(
      dragStart.value.panX + visualToLayout(event.clientX - dragStart.value.x),
      dragStart.value.panY + visualToLayout(event.clientY - dragStart.value.y)
    );
  };

  const onPointerUp = event => {
    if (!isDragging.value || dragStart.value?.pointerId !== event.pointerId) {
      return;
    }

    if (event.currentTarget?.hasPointerCapture?.(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    isDragging.value = false;
    dragStart.value = null;
  };

  const resetZoomAndRotation = () => {
    activeImageRotation.value = 0;
    setZoomLevel(ZOOM_LEVELS[0]);
    resetTransformOrigin();
  };

  return {
    zoomScale,
    imgTransformOriginPoint,
    activeImageRotation,
    panOffset,
    isDragging,
    imageWrapperStyle,
    imageStyle,
    resetTransformOrigin,
    onRotate,
    onZoom,
    setZoomScale,
    cancelPointerDrag,
    onDoubleClickZoomImage,
    onWheelImageZoom,
    onPointerDown,
    onPointerMove,
    onPointerUp,
    resetZoomAndRotation,
  };
};
